import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { SUPPORTED_LANGUAGES, MUSIC_GENRES, DIETARY_PREFERENCES, WELLNESS_GOALS, FAVORITE_ARTISTS, getFilteredArtists, getTranslation } from '../translations';
import { Check, User, Phone, Mail, MapPin, Lock, Globe } from 'lucide-react';
import { toast } from 'sonner';

const COUNTRIES = [
  { code: 'US', abbr: 'USA', name: 'United States', phoneCode: '+1', flag: '🇺🇸' },
  { code: 'GB', abbr: 'UK', name: 'United Kingdom', phoneCode: '+44', flag: '🇬🇧' },
  { code: 'IN', abbr: 'IND', name: 'India', phoneCode: '+91', flag: '🇮🇳' },
  { code: 'AE', abbr: 'UAE', name: 'United Arab Emirates', phoneCode: '+971', flag: '🇦🇪' },
  { code: 'SA', abbr: 'KSA', name: 'Saudi Arabia', phoneCode: '+966', flag: '🇸🇦' },
  { code: 'CA', abbr: 'CAN', name: 'Canada', phoneCode: '+1', flag: '🇨🇦' },
  { code: 'AU', abbr: 'AUS', name: 'Australia', phoneCode: '+61', flag: '🇦🇺' },
  { code: 'DE', abbr: 'GER', name: 'Germany', phoneCode: '+49', flag: '🇩🇪' },
  { code: 'FR', abbr: 'FRA', name: 'France', phoneCode: '+33', flag: '🇫🇷' },
  { code: 'JP', abbr: 'JPN', name: 'Japan', phoneCode: '+81', flag: '🇯🇵' },
  { code: 'CN', abbr: 'CHN', name: 'China', phoneCode: '+86', flag: '🇨🇳' },
  { code: 'BR', abbr: 'BRA', name: 'Brazil', phoneCode: '+55', flag: '🇧🇷' },
  { code: 'MX', abbr: 'MEX', name: 'Mexico', phoneCode: '+52', flag: '🇲🇽' },
  { code: 'IT', abbr: 'ITA', name: 'Italy', phoneCode: '+39', flag: '🇮🇹' },
  { code: 'ES', abbr: 'ESP', name: 'Spain', phoneCode: '+34', flag: '🇪🇸' },
  { code: 'KR', abbr: 'KOR', name: 'South Korea', phoneCode: '+82', flag: '🇰🇷' },
  { code: 'SG', abbr: 'SGP', name: 'Singapore', phoneCode: '+65', flag: '🇸🇬' },
  { code: 'PH', abbr: 'PHL', name: 'Philippines', phoneCode: '+63', flag: '🇵🇭' },
  { code: 'PK', abbr: 'PAK', name: 'Pakistan', phoneCode: '+92', flag: '🇵🇰' },
  { code: 'NG', abbr: 'NGA', name: 'Nigeria', phoneCode: '+234', flag: '🇳🇬' },
  { code: 'EG', abbr: 'EGY', name: 'Egypt', phoneCode: '+20', flag: '🇪🇬' },
  { code: 'ZA', abbr: 'RSA', name: 'South Africa', phoneCode: '+27', flag: '🇿🇦' },
  { code: 'TH', abbr: 'THA', name: 'Thailand', phoneCode: '+66', flag: '🇹🇭' },
  { code: 'MY', abbr: 'MYS', name: 'Malaysia', phoneCode: '+60', flag: '🇲🇾' },
  { code: 'ID', abbr: 'IDN', name: 'Indonesia', phoneCode: '+62', flag: '🇮🇩' },
  { code: 'TR', abbr: 'TUR', name: 'Turkey', phoneCode: '+90', flag: '🇹🇷' },
  { code: 'NL', abbr: 'NED', name: 'Netherlands', phoneCode: '+31', flag: '🇳🇱' },
  { code: 'SE', abbr: 'SWE', name: 'Sweden', phoneCode: '+46', flag: '🇸🇪' },
  { code: 'CH', abbr: 'SUI', name: 'Switzerland', phoneCode: '+41', flag: '🇨🇭' },
  { code: 'NZ', abbr: 'NZL', name: 'New Zealand', phoneCode: '+64', flag: '🇳🇿' }
];

// Cities by country code for dropdown
const CITIES_BY_COUNTRY = {
  'US': ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia', 'San Antonio', 'San Diego', 'Dallas', 'San Jose', 'Austin', 'Miami', 'Seattle', 'Denver', 'Boston', 'Atlanta', 'San Francisco', 'Portland', 'Las Vegas', 'Nashville'],
  'GB': ['London', 'Manchester', 'Birmingham', 'Leeds', 'Glasgow', 'Liverpool', 'Edinburgh', 'Bristol', 'Cardiff', 'Belfast', 'Sheffield', 'Nottingham', 'Cambridge', 'Oxford', 'Brighton'],
  'IN': ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Kolkata', 'Pune', 'Ahmedabad', 'Jaipur', 'Lucknow', 'Chandigarh', 'Kochi', 'Goa', 'Indore', 'Surat', 'Nagpur'],
  'AE': ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Al Ain', 'Umm Al Quwain'],
  'SA': ['Riyadh', 'Jeddah', 'Mecca', 'Medina', 'Dammam', 'Khobar', 'Tabuk', 'Abha'],
  'CA': ['Toronto', 'Vancouver', 'Montreal', 'Calgary', 'Ottawa', 'Edmonton', 'Winnipeg', 'Quebec City', 'Halifax'],
  'AU': ['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide', 'Gold Coast', 'Canberra', 'Hobart'],
  'DE': ['Berlin', 'Munich', 'Hamburg', 'Frankfurt', 'Cologne', 'Stuttgart', 'Dusseldorf', 'Hanover', 'Leipzig'],
  'FR': ['Paris', 'Lyon', 'Marseille', 'Toulouse', 'Nice', 'Bordeaux', 'Strasbourg', 'Lille', 'Nantes'],
  'JP': ['Tokyo', 'Osaka', 'Kyoto', 'Yokohama', 'Nagoya', 'Sapporo', 'Kobe', 'Fukuoka', 'Hiroshima'],
  'CN': ['Beijing', 'Shanghai', 'Guangzhou', 'Shenzhen', 'Chengdu', 'Hangzhou', 'Wuhan', 'Nanjing', 'Xi\'an'],
  'BR': ['Sao Paulo', 'Rio de Janeiro', 'Brasilia', 'Salvador', 'Belo Horizonte', 'Fortaleza', 'Curitiba'],
  'MX': ['Mexico City', 'Guadalajara', 'Monterrey', 'Puebla', 'Cancun', 'Tijuana', 'Merida'],
  'IT': ['Rome', 'Milan', 'Florence', 'Naples', 'Venice', 'Turin', 'Bologna', 'Palermo'],
  'ES': ['Madrid', 'Barcelona', 'Valencia', 'Seville', 'Bilbao', 'Malaga', 'Granada'],
  'KR': ['Seoul', 'Busan', 'Incheon', 'Daegu', 'Daejeon', 'Gwangju', 'Jeju'],
  'SG': ['Singapore Central', 'Jurong', 'Woodlands', 'Tampines', 'Bedok'],
  'PH': ['Manila', 'Cebu', 'Davao', 'Quezon City', 'Makati', 'Taguig'],
  'PK': ['Karachi', 'Lahore', 'Islamabad', 'Rawalpindi', 'Faisalabad', 'Peshawar'],
  'NG': ['Lagos', 'Abuja', 'Port Harcourt', 'Kano', 'Ibadan'],
  'EG': ['Cairo', 'Alexandria', 'Giza', 'Luxor', 'Aswan'],
  'ZA': ['Johannesburg', 'Cape Town', 'Durban', 'Pretoria', 'Port Elizabeth'],
  'TH': ['Bangkok', 'Chiang Mai', 'Phuket', 'Pattaya', 'Krabi'],
  'MY': ['Kuala Lumpur', 'Penang', 'Johor Bahru', 'Kota Kinabalu', 'Malacca'],
  'ID': ['Jakarta', 'Bali', 'Surabaya', 'Bandung', 'Yogyakarta', 'Medan'],
  'TR': ['Istanbul', 'Ankara', 'Izmir', 'Antalya', 'Bursa'],
  'NL': ['Amsterdam', 'Rotterdam', 'The Hague', 'Utrecht', 'Eindhoven'],
  'SE': ['Stockholm', 'Gothenburg', 'Malmo', 'Uppsala'],
  'CH': ['Zurich', 'Geneva', 'Bern', 'Basel', 'Lausanne'],
  'NZ': ['Auckland', 'Wellington', 'Christchurch', 'Hamilton', 'Queenstown'],
};

const AREAS_BY_CITY = {
  'Dubai': ['Downtown', 'Marina', 'JBR', 'Business Bay', 'DIFC', 'Palm Jumeirah', 'JLT', 'Deira', 'Bur Dubai', 'Al Barsha', 'Jumeirah', 'Silicon Oasis', 'Sports City', 'Motor City'],
  'Abu Dhabi': ['Corniche', 'Al Reem Island', 'Saadiyat Island', 'Yas Island', 'Khalidiya', 'Al Raha Beach'],
  'Mumbai': ['Bandra', 'Andheri', 'Juhu', 'Worli', 'Lower Parel', 'Powai', 'Colaba', 'Dadar', 'Goregaon'],
  'Delhi': ['Connaught Place', 'South Delhi', 'Dwarka', 'Saket', 'Hauz Khas', 'Greater Kailash', 'Vasant Kunj'],
  'London': ['Central London', 'Westminster', 'Kensington', 'Camden', 'Shoreditch', 'Canary Wharf', 'Greenwich', 'Soho'],
  'New York': ['Manhattan', 'Brooklyn', 'Queens', 'Bronx', 'Staten Island', 'Midtown', 'SoHo', 'Upper East Side', 'Williamsburg'],
  'Singapore Central': ['Orchard', 'Marina Bay', 'Raffles Place', 'Clarke Quay', 'Sentosa', 'Bugis'],
};

export default function OnboardingComplete({ onComplete, userEmail, userName, userSurname, initialLanguage = 'en', onEmailExists }) {
  const [step, setStep] = useState(0); // Start at 0 for email check
  const [emailCheckDone, setEmailCheckDone] = useState(false);
  const [preferences, setPreferences] = useState({
    language: initialLanguage,
    gender: '',
    phone: '',
    country_code: '+1',
    selected_country_code: 'US',
    city: '',
    area: '',
    country: '',
    music_preferences: [],
    artist_preferences: [],
    food_preferences: [],
    wellness_goals: []
  });
  
  const [emailOTP, setEmailOTP] = useState('');
  const [smsOTP, setSmsOTP] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpSent, setOtpSent] = useState({ email: false, sms: false });
  const [loading, setLoading] = useState(false);

  const t = (key) => getTranslation(key, preferences.language);

  // Check email doesn't already exist on mount
  useEffect(() => {
    const checkEmail = async () => {
      try {
        const res = await axios.post(`${API}/auth/check-email`, { email: userEmail });
        if (res.data.exists) {
          toast.error('This email is already registered. Please log in instead.');
          if (onEmailExists) onEmailExists();
          return;
        }
      } catch {}
      setEmailCheckDone(true);
      setStep(1);
    };
    checkEmail();
  }, [userEmail, onEmailExists]);

  const toggleSelection = (category, value) => {
    setPreferences(prev => {
      const current = prev[category];
      const newValues = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [category]: newValues };
    });
  };

  const sendEmailOTP = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-email-otp`, {
        identifier: userEmail,
        name: userName
      });
      setOtpSent({ ...otpSent, email: true });
      toast.success('OTP sent to your email!');
    } catch (error) {
      toast.error('Failed to send email OTP');
    } finally {
      setLoading(false);
    }
  };

  const sendSMSOTP = async () => {
    if (!preferences.phone || !preferences.country_code) {
      toast.error('Please enter your phone number');
      return;
    }
    
    setLoading(true);
    try {
      const fullPhone = `${preferences.country_code}${preferences.phone}`;
      await axios.post(`${API}/auth/send-sms-otp`, {
        identifier: fullPhone
      });
      setOtpSent({ ...otpSent, sms: true });
      toast.success('OTP sent to your phone!');
    } catch (error) {
      toast.error('Failed to send SMS OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailOTP = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-otp`, {
        identifier: userEmail,
        code: emailOTP
      });
      setEmailVerified(true);
      toast.success('Email verified!');
      setStep(4);
    } catch (error) {
      toast.error('Invalid OTP code');
    } finally {
      setLoading(false);
    }
  };

  const verifySMSOTP = async () => {
    setLoading(true);
    try {
      const fullPhone = `${preferences.country_code}${preferences.phone}`;
      await axios.post(`${API}/auth/verify-otp`, {
        identifier: fullPhone,
        code: smsOTP
      });
      setPhoneVerified(true);
      toast.success('Phone verified!');
      setStep(4);
    } catch (error) {
      toast.error('Invalid OTP code');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    // Step 2: Email verification (optional, can skip)
    // Step 2 is now whitelist → step 3 is OTP
    if (step === 3 && !emailVerified) {
      toast.error('Please verify your email before continuing');
      return;
    }
    
    // Gender(1) → Whitelist(2) → OTP(3) → Location(4) → ...
    if (step === 2) {
      setStep(3);
    } else if (step === 3) {
      setStep(4);
    } else if (step < 7) {
      setStep(step + 1);
    } else {
      onComplete({
        ...preferences,
        email_verified: emailVerified,
        phone_verified: false
      });
    }
  };

  const handleSkip = () => {
    onComplete({ 
      language: preferences.language,
      gender: preferences.gender || 'female'
    });
  };

  const handleCountryChange = (countryCode) => {
    const country = COUNTRIES.find(c => c.code === countryCode);
    if (country) {
      setPreferences({
        ...preferences,
        country: country.name,
        country_code: country.phoneCode
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-stone-50 to-stone-100 flex items-center justify-center p-4 md:p-6">
      <div className="zen-card p-6 md:p-10 max-w-2xl w-full">
        <div className="mb-6">
          <h2 className="text-2xl md:text-3xl font-semibold text-warm-charcoal" style={{ fontFamily: 'Playfair Display, serif' }}>
            Complete Your Profile
          </h2>
          {/* Progress bar - visual only, no step counter text */}
          <div className="flex gap-2 mt-4">
            {[1, 2, 3, 4, 5, 6].map((s) => {
              const actualStep = s <= 2 ? s : s + 1;
              return (
                <div
                  key={s}
                  className={`h-1 flex-1 rounded-full transition-all duration-500 ${
                    actualStep <= step ? 'bg-hunter-green' : 'bg-stone-200'
                  }`}
                />
              );
            })}
          </div>
        </div>

        {/* Step 0: Checking email... */}
        {step === 0 && (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="w-8 h-8 border-3 border-hunter-green border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm text-warm-charcoal/60">Verifying your email...</p>
            </div>
          </div>
        )}

        {/* Step 1: Gender */}
        {step === 1 && (
          <div className="space-y-6 fade-in">
            <Label className="text-sm font-medium text-warm-charcoal/80 uppercase tracking-wider mb-4 block">
              Select Your Gender
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => {
                  setPreferences({ ...preferences, gender: 'male' });
                  setTimeout(() => setStep(2), 300);
                }}
                className={`p-8 rounded-2xl border-2 transition-all duration-500 ${
                  preferences.gender === 'male'
                    ? 'border-hunter-green bg-hunter-green/10 shadow-lg scale-105'
                    : 'border-stone-200 hover:border-hunter-green/50'
                }`}
              >
                <div className="w-20 h-20 rounded-full bg-blue-100 mx-auto mb-4 flex items-center justify-center">
                  <User className="w-10 h-10 text-blue-600" />
                </div>
                <p className="font-semibold text-lg">Male</p>
              </button>
              <button
                onClick={() => {
                  setPreferences({ ...preferences, gender: 'female' });
                  setTimeout(() => setStep(2), 300);
                }}
                className={`p-8 rounded-2xl border-2 transition-all duration-500 ${
                  preferences.gender === 'female'
                    ? 'border-hunter-green bg-hunter-green/10 shadow-lg scale-105'
                    : 'border-stone-200 hover:border-hunter-green/50'
                }`}
              >
                <div className="w-20 h-20 rounded-full bg-pink-100 mx-auto mb-4 flex items-center justify-center">
                  <User className="w-10 h-10 text-pink-600" />
                </div>
                <p className="font-semibold text-lg">Female</p>
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Email Verification (OTP) */}
        {step === 3 && (
          <div className="space-y-6 fade-in">
            <button onClick={() => setStep(2)} className="flex items-center gap-1 text-sm text-warm-charcoal/60 hover:text-warm-charcoal mb-2">
              ← Back
            </button>
            
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-hunter-green" />
              <Label className="text-lg font-semibold">Verify Your Email</Label>
            </div>
            <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
              <p className="text-sm text-warm-charcoal/70">
                We'll send a verification code to: <strong>{userEmail}</strong>
              </p>
            </div>
            
            {!otpSent.email ? (
              <Button
                onClick={sendEmailOTP}
                disabled={loading}
                className="w-full zen-button bg-hunter-green text-white"
              >
                {loading ? 'Sending...' : 'Send Verification Code'}
              </Button>
            ) : (
              <div className="space-y-4">
                {/* Spam folder notice */}
                <div className="p-4 rounded-xl bg-amber-50/80 border border-amber-200/60">
                  <p className="text-xs text-amber-900 font-medium mb-1.5">Can't find the email?</p>
                  <ul className="text-[11px] text-amber-800/80 space-y-1 list-none">
                    <li>1. Check your <strong>Spam</strong> or <strong>Junk</strong> folder</li>
                    <li>2. Mark <strong className="text-amber-900">hello@contact.mindmymojo.com</strong> as a trusted sender</li>
                    <li>3. Wait a moment and tap "Resend Code" below</li>
                  </ul>
                </div>
                <Label className="text-sm font-medium text-warm-charcoal/80">
                  Enter 6-Digit Code
                </Label>
                <Input
                  type="text"
                  maxLength={6}
                  value={emailOTP}
                  onChange={(e) => setEmailOTP(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest font-bold"
                />
                <Button
                  onClick={verifyEmailOTP}
                  disabled={emailOTP.length !== 6 || loading}
                  className="w-full zen-button bg-hunter-green text-white"
                >
                  {loading ? 'Verifying...' : 'Verify Email'}
                </Button>
                <button
                  onClick={sendEmailOTP}
                  className="w-full text-sm text-hunter-green hover:underline"
                >
                  Resend Code
                </button>
              </div>
            )}
            <button
              onClick={() => setStep(4)}
              className="w-full text-sm text-warm-charcoal/50 hover:text-warm-charcoal mt-4 transition-colors"
            >
              Skip email verification
            </button>
          </div>
        )}

        {/* Step 2: Whitelist / Save Contact (BEFORE OTP) */}
        {step === 2 && (
          <div className="space-y-5 fade-in">
            <button onClick={() => setStep(1)} className="flex items-center gap-1 text-sm text-warm-charcoal/60 hover:text-warm-charcoal mb-2">
              ← Back
            </button>
            
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-hunter-green/10 mx-auto mb-3 flex items-center justify-center">
                <Mail className="w-7 h-7 text-hunter-green" />
              </div>
              <Label className="text-lg font-semibold block" style={{ fontFamily: 'Playfair Display, serif' }}>Stay Connected</Label>
              <p className="text-xs text-warm-charcoal/60 mt-2 leading-relaxed max-w-xs mx-auto">
                To make sure our wellness reminders, check-in nudges, and updates reach your inbox (and not your spam folder), save our contact.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-hunter-green/5 border border-hunter-green/20">
              <p className="text-sm font-medium text-warm-charcoal mb-1">Save this contact:</p>
              <p className="text-hunter-green font-semibold text-sm">hello@contact.mindmymojo.com</p>
            </div>

            {/* Save to contacts button */}
            <Button
              onClick={() => {
                // Create a vCard file for download
                const vcard = `BEGIN:VCARD
VERSION:3.0
FN:MindMyMojo
ORG:MindMyMojo
EMAIL;TYPE=INTERNET:hello@contact.mindmymojo.com
URL:https://mindmymojo.com
NOTE:Emotional Wellness Companion
END:VCARD`;
                const blob = new Blob([vcard], { type: 'text/vcard' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'MindMyMojo.vcf';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                toast.success('Contact file downloaded! Open it to save to your contacts.');
                setTimeout(() => setStep(3), 1500);
              }}
              className="w-full zen-button bg-hunter-green text-white hover:bg-hunter-green/90 h-11"
            >
              Save MindMyMojo to Contacts
            </Button>

            {/* Skip option with warning */}
            <div className="text-center">
              <button
                onClick={() => {
                  if (window.confirm('Skipping this step may cause emails from MindMyMojo to go to your spam folder. Do you want to proceed without saving?')) {
                    setStep(3);
                  }
                }}
                className="text-xs text-warm-charcoal/40 hover:text-warm-charcoal/60 transition-colors"
                style={{ touchAction: 'manipulation' }}
              >
                Skip this step
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Location (dropdowns) */}
        {step === 4 && (
          <div className="space-y-6 fade-in">
            <button onClick={() => setStep(3)} className="flex items-center gap-1 text-sm text-warm-charcoal/60 hover:text-warm-charcoal mb-2">
              ← Back
            </button>
            
            <div className="flex items-center gap-3 mb-4">
              <Globe className="w-6 h-6 text-hunter-green" />
              <Label className="text-lg font-semibold">Your Location</Label>
            </div>
            <p className="text-sm text-warm-charcoal/60">
              Optional — helps us connect you with your local community
            </p>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-medium text-warm-charcoal/70 mb-2 block">Country</Label>
                <select
                  value={COUNTRIES.find(c => c.name === preferences.country)?.code || ''}
                  onChange={(e) => {
                    handleCountryChange(e.target.value);
                    setPreferences(p => ({ ...p, city: '', area: '' }));
                  }}
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white focus:border-hunter-green/50 focus:ring-2 focus:ring-hunter-green/10 text-sm"
                >
                  <option value="">Select country (optional)</option>
                  {COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                  ))}
                </select>
              </div>
              
              {preferences.country && (
                <div>
                  <Label className="text-xs font-medium text-warm-charcoal/70 mb-2 block">City</Label>
                  <select
                    value={preferences.city}
                    onChange={(e) => setPreferences(p => ({ ...p, city: e.target.value, area: '' }))}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white focus:border-hunter-green/50 focus:ring-2 focus:ring-hunter-green/10 text-sm"
                  >
                    <option value="">Select city (optional)</option>
                    {(CITIES_BY_COUNTRY[COUNTRIES.find(c => c.name === preferences.country)?.code] || []).map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {preferences.city && AREAS_BY_CITY[preferences.city] && (
                <div>
                  <Label className="text-xs font-medium text-warm-charcoal/70 mb-2 block">Neighbourhood</Label>
                  <select
                    value={preferences.area}
                    onChange={(e) => setPreferences(p => ({ ...p, area: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white focus:border-hunter-green/50 focus:ring-2 focus:ring-hunter-green/10 text-sm"
                  >
                    <option value="">Select area (optional)</option>
                    {AREAS_BY_CITY[preferences.city].map(area => (
                      <option key={area} value={area}>{area}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <Button
              onClick={handleNext}
              className="w-full zen-button bg-hunter-green text-white"
            >
              Continue
            </Button>
          </div>
        )}
        {/* Step 5: Language */}
        {step === 5 && (
          <div className="space-y-6 fade-in">
            {/* Back button */}
            <button
              onClick={() => setStep(4)}
              className="flex items-center gap-1 text-sm text-warm-charcoal/60 hover:text-warm-charcoal mb-2"
            >
              ← Back
            </button>
            
            <Label className="text-sm font-medium text-warm-charcoal/80 uppercase tracking-wider mb-4 block">
              {t('language_preference')}
            </Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setPreferences({ ...preferences, language: lang.code })}
                  className={`p-4 rounded-xl border-2 transition-all duration-500 ${
                    preferences.language === lang.code
                      ? 'border-hunter-green bg-hunter-green/5'
                      : 'border-stone-200 hover:border-hunter-green/30'
                  }`}
                >
                  <p className="font-semibold text-sm">{lang.nativeName}</p>
                  <p className="text-xs text-warm-charcoal/50 mt-1">{lang.name}</p>
                </button>
              ))}
            </div>
            <Button onClick={handleNext} className="w-full zen-button bg-hunter-green text-white mt-6">
              Continue
            </Button>
          </div>
        )}

        {/* Step 6: Music Preferences */}
        {step === 6 && (
          <div className="space-y-6 fade-in">
            {/* Back button */}
            <button
              onClick={() => setStep(5)}
              className="flex items-center gap-1 text-sm text-warm-charcoal/60 hover:text-warm-charcoal mb-2"
            >
              ← Back
            </button>
            
            <Label className="text-sm font-medium text-warm-charcoal/80 uppercase tracking-wider mb-2 block">
              {t('music_preferences')}
            </Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {MUSIC_GENRES.map((genre) => (
                <button
                  key={genre.value}
                  onClick={() => toggleSelection('music_preferences', genre.value)}
                  className={`p-4 rounded-xl border-2 transition-all duration-500 ${
                    preferences.music_preferences.includes(genre.value)
                      ? 'border-hunter-green bg-hunter-green/5'
                      : 'border-stone-200 hover:border-hunter-green/30'
                  }`}
                >
                  {preferences.music_preferences.includes(genre.value) && (
                    <Check className="w-4 h-4 text-hunter-green mb-1" />
                  )}
                  <p className="font-semibold text-sm">{genre.label}</p>
                </button>
              ))}
            </div>
            <Label className="text-sm font-medium text-warm-charcoal/80 uppercase tracking-wider mt-4 mb-2 block">
              Favorite Artists <span className="text-xs normal-case text-warm-charcoal/50">(Optional - Based on your genre selection)</span>
            </Label>
            {preferences.music_preferences.length > 0 ? (
              <p className="text-xs text-hunter-green/70 mb-2">
                Showing artists for: {preferences.music_preferences.join(', ')}
              </p>
            ) : (
              <p className="text-xs text-warm-charcoal/50 mb-2">
                Select music genres above to see relevant artists
              </p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-40 overflow-y-auto border border-stone-200 rounded-lg p-2">
              {getFilteredArtists(preferences.music_preferences).map((artist) => (
                <button
                  key={artist.value}
                  onClick={() => toggleSelection('artist_preferences', artist.value)}
                  className={`p-2 rounded-lg border transition-all duration-300 ${
                    preferences.artist_preferences.includes(artist.value)
                      ? 'border-hunter-green bg-hunter-green/5'
                      : 'border-stone-200 hover:border-hunter-green/30'
                  }`}
                >
                  {preferences.artist_preferences.includes(artist.value) && (
                    <Check className="w-3 h-3 text-hunter-green mb-0.5" />
                  )}
                  <p className="font-medium text-xs">{artist.label}</p>
                </button>
              ))}
            </div>
            <Button onClick={handleNext} className="w-full zen-button bg-hunter-green text-white mt-4">
              Continue
            </Button>
          </div>
        )}

        {/* Step 7: Wellness Goals */}
        {step === 7 && (
          <div className="space-y-6 fade-in">
            {/* Back button */}
            <button
              onClick={() => setStep(6)}
              className="flex items-center gap-1 text-sm text-warm-charcoal/60 hover:text-warm-charcoal mb-2"
            >
              ← Back
            </button>
            
            <Label className="text-sm font-medium text-warm-charcoal/80 uppercase tracking-wider mb-2 block">
              {t('wellness_goals')}
            </Label>
            <div className="grid grid-cols-2 gap-3">
              {WELLNESS_GOALS.map((goal) => (
                <button
                  key={goal.value}
                  onClick={() => toggleSelection('wellness_goals', goal.value)}
                  className={`p-4 rounded-xl border-2 transition-all duration-500 ${
                    preferences.wellness_goals.includes(goal.value)
                      ? 'border-hunter-green bg-hunter-green/5'
                      : 'border-stone-200 hover:border-hunter-green/30'
                  }`}
                >
                  {preferences.wellness_goals.includes(goal.value) && (
                    <Check className="w-4 h-4 text-hunter-green mb-1" />
                  )}
                  <p className="font-semibold text-sm">{goal.label}</p>
                </button>
              ))}
            </div>
            <Button onClick={handleNext} className="w-full zen-button bg-hunter-green text-white mt-6">
              Complete Setup
            </Button>
          </div>
        )}

      </div>
    </div>
  );
}
